import { FC, useCallback, useEffect, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from 'components/UI/Button';
import { InputField } from 'components/UI/InputField';
import { Modal } from 'components/UI/Modal';
import { UserGroups } from 'components/UI/UserGroups';
import { useTranslations } from 'hooks/useTranslations';
import { ExtendedUser } from 'models/diam';
import { useUpdatePasswordMutation } from 'store/diamApi';
import { defaultPasswordPolicy } from 'utils/constants';
import { getUserGroups } from 'utils/userUtils';
import { showSuccessToast } from 'utils/toastUtils';
import {
  Description,
  InputFieldContainer,
  UserGroupsContainer
} from './styled';

type Props = {
  user: ExtendedUser;
  isOpen: boolean;
  close: () => void;
};

export const ChangePasswordModal: FC<Props> = ({ user, isOpen, close }) => {
  const t = useTranslations();
  const [updatePassword, updatePasswordResult] = useUpdatePasswordMutation();

  const minimumLength = useMemo(
    () =>
      user.includedPasswordPolicy?.minimumLength ??
      defaultPasswordPolicy.minimumLength,
    [user.includedPasswordPolicy?.minimumLength]
  );

  const maximumLength = useMemo(
    () =>
      user.includedPasswordPolicy?.maximumLength ??
      defaultPasswordPolicy.maximumLength,
    [user.includedPasswordPolicy?.maximumLength]
  );

  // Zod schema
  const schema = useMemo(
    () =>
      z
        .object({
          oldPassword: z.string().min(1, t('validation.required')),
          newPassword: z
            .string()
            .min(1, t('validation.required'))
            .min(
              minimumLength,
              t('validation.password_minimum_length', { minimumLength })
            )
            .max(
              maximumLength,
              t('validation.password_maximum_length', { maximumLength })
            ),
          confirmPassword: z.string().min(1, t('validation.required'))
        })
        .refine((data) => data.newPassword === data.confirmPassword, {
          message: t('validation.confirm_password'),
          path: ['confirmPassword']
        }),
    [t, minimumLength, maximumLength]
  );

  type FormData = z.infer<typeof schema>;

  // useForm
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(schema)
  });

  // Close
  const onClose = useCallback(() => {
    close();
    reset();
  }, [close, reset]);

  // Submit
  const onSubmit: SubmitHandler<FormData> = useCallback(
    ({ oldPassword, newPassword }) => {
      updatePassword({
        oldPassword,
        newPassword
      });
    },
    [updatePassword]
  );

  useEffect(() => {
    if (updatePasswordResult.isSuccess) {
      showSuccessToast(t('account_security.toast_password_change_successful'));
      onClose();
    }
  }, [onClose, t, updatePasswordResult.isSuccess]);

  return (
    <Modal
      title={t('account_security.action_change_password')}
      isOpen={isOpen}
      isLoading={updatePasswordResult.isLoading}
      onClose={onClose}
    >
      <Description>
        {t('account_security.description_change_password')}
      </Description>
      <Description>
        <strong>{user.username}</strong>
      </Description>
      <Description>{t('account_security.description_services')}</Description>
      <UserGroupsContainer>
        <UserGroups groups={getUserGroups(user)} />
      </UserGroupsContainer>
      <Description>
        {t('account_security.description_change_password_logout')}
      </Description>
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputFieldContainer>
          <InputField
            id="oldPassword"
            label={t('account_security.label_current_password')}
            required
            isPasswordInput
            placeholder={t('account_security.placeholder_current_password')}
            error={errors.oldPassword}
            register={register('oldPassword')}
          />
          <InputField
            id="newPassword"
            label={t('account_security.label_new_password')}
            required
            isPasswordInput
            placeholder={t('account_security.placeholder_new_password')}
            error={errors.newPassword}
            register={register('newPassword')}
          />
          <InputField
            id="confirmPassword"
            label={t('account_security.label_confirm_password')}
            required
            isPasswordInput
            placeholder={t('account_security.placeholder_confirm_password')}
            error={errors.confirmPassword}
            register={register('confirmPassword')}
          />
        </InputFieldContainer>
        <Button type="submit">{t('account_security.set_password')}</Button>
      </form>
    </Modal>
  );
};
